import { APIClient, ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import {
    CreateGroup,
    CreateGroupMember,
    GroupListResponse,
    GroupMember,
    GroupMemberListResponse,
    GroupMemberQueryParams,
    GroupQueryParams,
    IGroup,
    UpdateGroup,
    UpdateGroupMember
} from '@frontend/group/types';

export class ProductCategoryClient extends APIClient {
    public static readonly endpoint = '/group-api/v1';

    public static async fetchProductCategories(queryParams?: ApiQueryParams<GroupQueryParams>): Promise<GroupListResponse> {
        return await this.apiPaginated<GroupListResponse, GroupQueryParams>(`${this.endpoint}/product-categories`, queryParams);
    }

    public static async resolveProductCategories(ids: string[], field: string = 'id'): Promise<GroupListResponse> {
        return this.resolve<GroupListResponse>(`${this.endpoint}/product-categories`, ids, field);
    }

    public static async deleteProductCategories(groupIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/product-categories-delete`, { ids: groupIds });
        return await this.handleVoidResponse(response);
    }

    public static async postProductCategory(accountId: string, group: CreateGroup): Promise<IGroup> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/product-categories`, group);
        return await this.handleResponse<IGroup>(response);
    }

    public static async fetchProductCategory(accountId: string, groupId: string): Promise<IGroup> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/product-categories/${groupId}`);
        return await this.handleResponse<IGroup>(response);
    }

    public static async patchProductCategory(accountId: string, groupId: string, group: UpdateGroup): Promise<IGroup> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/product-categories/${groupId}`, group);
        return await this.handleResponse<IGroup>(response);
    }

    public static async deleteProductCategory(accountId: string, groupId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/product-categories/${groupId}`);
        return await this.handleVoidResponse(response);
    }

    public static async fetchGroupsNotInGroup(
        accountId: string,
        groupId: string,
        queryParams: ApiQueryParams<DefaultQueryParams | GroupQueryParams>
    ): Promise<GroupListResponse> {
        return await this.apiPaginated<GroupListResponse, DefaultQueryParams & GroupQueryParams>(
            `${this.endpoint}/accounts/${accountId}/product-categories/${groupId}/groups`,
            queryParams
        );
    }

    //IGroup members
    public static async fetchProductCategoryMembers(queryParams?: ApiQueryParams<GroupMemberQueryParams>): Promise<GroupMemberListResponse> {
        return await this.apiPaginated<GroupMemberListResponse, GroupMemberQueryParams>(`${this.endpoint}/product-category-members`, queryParams);
    }

    public static async resolveProductCategoryMembers(ids: string[], field: string = 'id'): Promise<GroupMemberListResponse> {
        return this.resolve<GroupMemberListResponse>(`${this.endpoint}/product-category-members`, ids, field);
    }

    public static async deleteProductCategoryMembers(memberIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/product-category-members-delete`, { ids: memberIds });
        return await this.handleVoidResponse(response);
    }

    public static async postProductCategoryMember(accountId: string, groupId: string, groupMember: CreateGroupMember): Promise<GroupMember> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/product-categories/${groupId}/members`, groupMember);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async postProductCategoryMemberList(accountId: string, groupId: string, entity_type: EntityType, memberIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/product-categories/${groupId}/${entity_type}s/members-list`, {
            ids: memberIds
        });
        return await this.handleVoidResponse(response);
    }

    public static async fetchProductCategoryMember(accountId: string, groupId: string, memberId: string): Promise<GroupMember> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/product-categories/${groupId}/members/${memberId}`);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async patchProductCategoryMember(accountId: string, groupId: string, memberId: string, body: UpdateGroupMember): Promise<GroupMember> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/product-categories/${groupId}/members/${memberId}`, body);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async deleteProductCategoryMember(accountId: string, groupId: string, memberId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/product-categories/${groupId}/members/${memberId}`);
        return await this.handleVoidResponse(response);
    }

    public static async fetchGroupMembersRecursively(accountId: string, groupId: string): Promise<GroupMemberListResponse> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/product-categories/${groupId}/members-recursively`);
        return await this.handleResponse<GroupMemberListResponse>(response);
    }
}
